import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { chaptersApi, projectsApi, novelBuildApi } from '../api';
import { streamSSE } from '../auth_api';
import { celebrateChapterFinished } from '../celebrate';

/** Compute word count from raw content when the stored `words` field is zero/stale. */
function _wordsFromContent(content) {
  if (!content || !content.trim()) return 0;
  return content.trim().split(/\s+/).filter(Boolean).length;
}

/** Long-running "rebuild novel" task + per-chapter streaming generation. */
export function useNovelBuild({ project, setProject, setChapters, setActiveChapterId, loadProject, onTokensUsed }) {
  // ----- rebuild -----
  const [building, setBuilding]       = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  /** Live per-chapter job data from the background job status.
   *  Shape: { status, current_chapter, completed: number[], total_chapters } */
  const [buildJob, setBuildJob] = useState(null);

  const rebuildNovel = useCallback(async ({ guidance = '', includeExisting = false } = {}) => {
    if (!project) return;
    setBuilding(true);
    setBuildProgress(3);
    setBuildJob(null);
    const total = project.chapters_count || 20;
    let pollTimer = null;
    try {
      await novelBuildApi.start(project.id, {
        mode:              'sequential',
        pause_after:       total,
        use_voice_profile: true,
      });
      toast.info(`Build started — drafting ${total} chapter${total === 1 ? '' : 's'}…`);

      // Poll every 3.5 s — feed chapter-level updates into buildJob
      let done = false;
      while (!done) {
        await new Promise((r) => { pollTimer = setTimeout(r, 3500); });
        let status;
        try { status = await novelBuildApi.status(project.id); } catch { break; }
        if (!status) break;

        // Expose raw job data for the wizard UI
        setBuildJob(status);

        const completed = (status.completed || []).length;
        setBuildProgress(Math.min(92, Math.round((completed / Math.max(total, 1)) * 92) + 3));
        if (['done', 'error', 'cancelled'].includes(status.status)) done = true;
      }

      const [chs, p] = await Promise.all([
        chaptersApi.list(project.id),
        projectsApi.get(project.id),
      ]);
      // Use loadProject if available — it re-initialises the whole workspace
      // in one pass (chapters + project + activeChapterId) preventing the
      // brief blank-workspace flash caused by two separate state updates.
      if (loadProject) {
        await loadProject(project.id, { silent: true });
      } else {
        setChapters(chs.map((c) => ({
          id: c.id, number: c.number, title: c.title, words: c.words || _wordsFromContent(c.content),
          status: c.status, content: c.content, sentiment: c.sentiment,
        })));
        setProject(p);
      }
      setBuildProgress(100);
      const totalWords = chs.reduce((sum, c) => sum + (c.words || 0), 0);
      toast.success(`Novel rebuilt — ${totalWords.toLocaleString()} words across ${total} chapters.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' ? detail.message : detail)
        || 'Build failed. Check that you have a bulk-draft capable plan.';
      toast.error(msg);
    } finally {
      if (pollTimer) clearTimeout(pollTimer);
      setTimeout(() => { setBuilding(false); setBuildProgress(0); setBuildJob(null); }, 800);
    }
  }, [project, setChapters, setProject, loadProject]);

  // ----- per-chapter streaming -----
  const [streamingChapterId, setStreamingChapterId] = useState(null);
  const [streamingText, setStreamingText] = useState('');

  const generateChapter = useCallback(async (chapterId, guidance = '') => {
    if (!project) return;
    setStreamingChapterId(chapterId);
    setStreamingText('');
    let finalChapter = null;
    let buf = '';
    let streamError = null;
    try {
      await streamSSE('/stream/generate/chapter',
        { project_id: project.id, chapter_id: chapterId, user_guidance: guidance },
        {
          onEvent: (ev) => {
            if (ev.event === 'meta') {
              toast.info(`Drafting Chapter ${ev.data.number}\u2026 (target ~${ev.data.target_words.toLocaleString()} words)`);
              if (ev.data.model_fallback && ev.data.requested_model_id !== ev.data.model_id) {
                toast.warning(
                  `Your selected model is disabled — drafting with ${ev.data.model_id} instead. Update in Settings → AI.`,
                  { duration: 6000 }
                );
              }
            } else if (ev.event === 'delta') {
              buf += ev.data.text;
              setStreamingText(buf);
            } else if (ev.event === 'done') {
              finalChapter = ev.data.chapter;
              // Notify the StatusBar cache indicator
              window.dispatchEvent(new CustomEvent('cr-cache-status', {
                detail: {
                  read:      ev.data.cache_read  || 0,
                  write:     ev.data.cache_write || 0,
                  saved_usd: ev.data.saved_usd   || 0,
                },
              }));
            } else if (ev.event === 'error') {
              streamError = ev.data?.message || 'Generation error.';
              toast.error(`Generation error: ${streamError}`);
            }
          },
        });
    } catch (e) {
      // Network / ingress dropped the SSE mid-stream. The backend
      // persists inside the async generator AFTER the last delta, so
      // there's a good chance the chapter DID land in Mongo — we just
      // never received the `done` frame. Fall through to the refetch
      // safety net below so the writer never loses their draft.
      streamError = e?.message || 'Connection dropped mid-stream.';
      console.warn('[generateChapter] stream ended abruptly:', streamError);
    }
    setStreamingChapterId(null);
    setStreamingText('');
    // iter55g25 — Refetch chapters if we saw ANY generated content
    // even when the `done` event never arrived (Cloudflare timeout,
    // proxy hiccup, network flake). Guarantees the user never loses
    // a freshly-drafted chapter to a dropped SSE frame.
    const shouldRefetch = Boolean(finalChapter) || buf.length > 0;
    if (shouldRefetch) {
      try {
        const chs = await chaptersApi.list(project.id);
        setChapters(chs.map((c) => ({
          id: c.id, number: c.number, title: c.title, words: c.words || _wordsFromContent(c.content),
          status: c.status, content: c.content, sentiment: c.sentiment,
        })));
        const refreshed = chs.find((c) => c.id === chapterId);
        setActiveChapterId((finalChapter?.id) || chapterId);
        if (finalChapter) {
          toast.success(`Chapter ${finalChapter.number} drafted (${finalChapter.words} words).`);
          celebrateChapterFinished();
          // Nudge writer to name the chapter if it still has a generic title
          const genericTitle = !finalChapter.title || /^chapter\s*\d+$/i.test(finalChapter.title.trim());
          if (genericTitle) {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('pw:chapter-draft-complete', { detail: { chapter: finalChapter } }));
            }, 2200); // wait for confetti to settle
          }
        } else if (refreshed?.content && refreshed.content.trim()) {
          toast.success(`Chapter ${refreshed.number} recovered (${refreshed.words || 0} words). The stream was interrupted but the draft was saved.`);
          celebrateChapterFinished();
          // Nudge for recovered chapters with generic titles too
          const genericTitle = !refreshed.title || /^chapter\s*\d+$/i.test((refreshed.title || '').trim());
          if (genericTitle) {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('pw:chapter-draft-complete', { detail: { chapter: refreshed } }));
            }, 2200);
          }
        } else if (!streamError) {
          toast.warning('Draft stream ended before persisting — nothing to recover.');
        }
        // iter227 — Let callers (e.g. the first-run wizard) know the
        // real outcome synchronously. Reading `chapters` state right
        // after this `await` resolves is unreliable — React hasn't
        // necessarily committed the `setChapters` above yet.
        if (typeof onTokensUsed === 'function') onTokensUsed();
        return {
          success: Boolean(finalChapter) || Boolean(refreshed?.content?.trim()),
          words: finalChapter?.words ?? refreshed?.words ?? 0,
        };
      } catch (e) {
        console.warn('[generateChapter] refetch after stream failed', e);
      }
    }
    // iter-53n++ — Refresh the token budget from /api/usage/me so the
    // Buy-Tokens tier and cost badges reflect the real balance.
    if (typeof onTokensUsed === 'function') onTokensUsed();
    return { success: false, words: 0 };
  }, [project, setChapters, setActiveChapterId, onTokensUsed]);

  return {
    building, buildProgress, buildJob, rebuildNovel,
    streamingChapterId, streamingText, generateChapter,
  };
}
